import React from 'react';
import classes from './style.module.scss';
import useTranslation from "~/app/hooks/useTranslation";
import Icon from "~/app/common/Icon";
import { TrainingDetail, TRAINING_STATUS } from '~/training/types';
import { useStoreState } from '~/store';
import Loader from '~/app/common/Loader';
import ParticipantList from '../Participant';
import CollapsibleSection from '../CollapsibleSection';
import InstructorInfo from '../InstructorInfo';
import CommentText from '../CommentText';
import TrainingChecklist from '../TrainingChecklist';
import LinkedMachines from '../LinkedMachines';
import ExternalResources from '../ExternalResources';
import TrainingAttachments from '../TrainingAttachments';
import TrainingActions from '../TrainingActions';

export interface TrainingProps {
  training: TrainingDetail
}

export type TrainingComponent = React.FC<TrainingProps>

const Training: TrainingComponent = ({ training }) => {
  const fetching = useStoreState(state => state.training.fetching);
  const user = useStoreState(state => state.user.data);

  const isInstructorViewer = String(user?.id ?? '') === String(training?.instructor?.id ?? '');

  const { t } = useTranslation("training");

  if (fetching) {
    return <Loader size="32" />
  }

  return (
    <div className={classes['training-info']}>
      <div className={classes['info-wrapper']}>
        <div className={classes['title']}>{t('title')}</div>
        <div className={classes['value']}>{training?.title}</div>

        <div className={classes['title']}>{t('trainingDate')}</div>
        <div className={classes['value']}>{training?.trainingDate}</div>
        {training?.status !== undefined && training?.status !== null && training.status !== TRAINING_STATUS.NOTAPPLICABLE &&
          <>
            <div className={classes['title']}>{t("statusTitle")}</div>
            <div className={classes['value']}>
              <Icon name="stop_circle" className={`${classes['status']} ${classes[training.status]}`} />
              {t(`status.${training.status}`)}
            </div>
          </>
        }

        <div className={classes['title']}>{t("instructor")}</div>
        {training?.instructor && <InstructorInfo instructor={training.instructor} />}
        <div className={classes['title']}>{t("commentFromInstructor")}</div>
        <div className={classes['value']}>
          <CommentText value={training?.commentFromInstructor} />
        </div>
      </div>
      <hr className={classes['bottom-line']} />

      <TrainingChecklist checklist={training?.checklist ?? []} />

      <CollapsibleSection title={t('linkedMachines')}>
        <LinkedMachines excavators={training?.excavators ?? []} />
      </CollapsibleSection>

      <CollapsibleSection title={t('external.externalContent')}>
        <ExternalResources resources={training?.externalResources ?? []} />
      </CollapsibleSection>

      <CollapsibleSection title={t('attachments.title')}>
        <TrainingAttachments images={training?.images ?? []} pdfs={training?.pdfs ?? []} />
      </CollapsibleSection>

      <CollapsibleSection title={t('participants')}>
        <ParticipantList
          participants={training?.participants ?? []}
          showDetails={isInstructorViewer}
        />
      </CollapsibleSection>

      {!isInstructorViewer && (
        <div className={classes['section']}>
          <div className={classes['title']}>{t('comment')}</div>
          <div className={classes['value']}>
            <CommentText value={training?.comment} />
          </div>
        </div>
      )}

      {training && <TrainingActions training={training} />}
    </div>
  );
};

export default Training
